import { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  Position,
  useReactFlow,
  useStore,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { collaborationToReactFlow, isGraphDefinition } from '../../utils/definitionTransform';
import type { CollaborationDefinition } from '../../types/definition';
import type { StepState, Turn, TurnStep } from '../../types/runExplorer';
import { STEP_STATE_STYLES, formatMs } from './runExplorerShared';
import { PATH_FIT_MAX_ZOOM, PATH_FIT_PADDING, graphExtent, pathCanvasHeight } from '../../utils/graphCanvasHeight';

interface RunStepNodeData {
  stepId: string;
  label: string;
  nodeType: string;
  state: StepState;
  caption: string;
  selected: boolean;
  [key: string]: unknown;
}

type RunStepNode = Node<RunStepNodeData, 'runStep'>;

/** What a node's second line says about the step in this turn. */
function captionFor(step: TurnStep | undefined): string {
  if (!step) return 'not reached';
  switch (step.state) {
    case 'skipped':
      return step.skip?.branchStepId ? `skipped by ${step.skip.branchStepId}` : 'skipped';
    case 'not-reached':
      return 'not reached';
    case 'failed':
      return 'failed';
    default: {
      const model = step.modelCalls[step.modelCalls.length - 1]?.llmModel;
      const selectedBranch = step.resultEntry?.output?.selectedBranch;
      const parts = [
        step.nodeType,
        typeof selectedBranch === 'string' ? selectedBranch : model ?? null,
        formatMs(step.durationMs),
      ].filter(Boolean);
      return parts.join(' · ');
    }
  }
}

/**
 * A step on the run's path: the same graph the config editor draws, coloured by
 * what happened to the step in the selected turn. No handles can be dragged and
 * nothing here writes.
 */
const RunStepNodeView = memo(function RunStepNodeView({ data }: NodeProps<RunStepNode>) {
  const style = STEP_STATE_STYLES[data.state];
  return (
    <div
      data-testid={`path-node-${data.stepId}`}
      data-state={data.state}
      className={`px-3 py-2 rounded-lg border-2 min-w-[160px] max-w-[200px] shadow-sm ${style.node} ${
        data.selected ? 'ring-4 ring-indigo-300 dark:ring-indigo-700' : ''
      }`}
    >
      <Handle type="target" position={Position.Left} isConnectable={false} className="!w-2 !h-2 !bg-gray-400" />
      <div className="flex items-center gap-1.5">
        <span className={`h-2 w-2 rounded-full flex-shrink-0 ${style.dot}`} aria-hidden="true" />
        <span className="text-sm font-semibold font-mono text-gray-900 dark:text-gray-100 truncate">{data.stepId}</span>
      </div>
      <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-300 truncate">{data.caption}</div>
      <Handle type="source" position={Position.Right} isConnectable={false} className="!w-2 !h-2 !bg-gray-400" />
    </div>
  );
});

const nodeTypes = { runStep: RunStepNodeView };

interface RunPathCanvasProps {
  definition: CollaborationDefinition | null;
  turn: Turn | null;
  selectedStepId: string | null;
  onSelectStep: (stepId: string) => void;
}

function RunPathCanvasInner({ definition, turn, selectedStepId, onSelectStep }: RunPathCanvasProps) {
  const [pathOnly, setPathOnly] = useState(false);
  const { fitView } = useReactFlow();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState(0);
  const [windowHeight, setWindowHeight] = useState(() => window.innerHeight);

  const laidOut = useMemo(
    () => collaborationToReactFlow(isGraphDefinition(definition) ? definition : null),
    [definition]
  );

  const { nodes, edges } = useMemo(() => {
    const stateOf = (id: string): StepState => turn?.steps[id]?.state ?? 'not-reached';
    const ran = new Set(turn?.path ?? []);
    // Path only shows the steps on the turn's path; skipped and not-reached
    // steps are hidden. With no turn there is no path, so nothing is hidden.
    const hidden = new Set(
      pathOnly && turn ? laidOut.nodes.filter((node) => !ran.has(node.id)).map((node) => node.id) : []
    );
    const order = new Map((turn?.path ?? []).map((id, i) => [id, i]));

    const flowNodes: RunStepNode[] = laidOut.nodes
      .filter((node) => !hidden.has(node.id))
      .map((node) => ({
        id: node.id,
        type: 'runStep' as const,
        position: node.position,
        draggable: false,
        connectable: false,
        selectable: true,
        data: {
          stepId: node.id,
          label: node.data.label,
          nodeType: node.data.nodeType,
          state: stateOf(node.id),
          caption: captionFor(turn?.steps[node.id]),
          selected: node.id === selectedStepId,
        },
      }));

    const flowEdges: Edge[] = laidOut.edges
      .filter((edge) => !hidden.has(edge.source) && !hidden.has(edge.target))
      .map((edge) => {
        // An edge carried data when both ends ran and the source ran first.
        const carried =
          ran.has(edge.source) && ran.has(edge.target) && (order.get(edge.source) ?? 0) < (order.get(edge.target) ?? 0);
        return {
          id: edge.id,
          source: edge.source,
          target: edge.target,
          type: 'smoothstep',
          animated: false,
          data: { carried },
          style: carried
            ? { stroke: '#6366f1', strokeWidth: 2.5 }
            : { stroke: '#9ca3af', strokeWidth: 1, opacity: 0.45 },
        };
      });

    return { nodes: flowNodes, edges: flowEdges };
  }, [laidOut, turn, pathOnly, selectedStepId]);

  const pathOnlyAvailable = turn !== null;
  const height = pathCanvasHeight(graphExtent(nodes), cardWidth, windowHeight);

  // The card grows to the graph, so it needs its own width and the window's height.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;
    const measure = () => {
      setCardWidth(wrapper.getBoundingClientRect().width);
      setWindowHeight(window.innerHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [laidOut]);

  // React Flow's own measurement of its pane height. It updates after the card's
  // inline height lands, so the refit keys on it and not on `height`: fitting on
  // `height` used the previous pane size and left the graph clipped or off-centre
  // (task 1324), and keeping both scheduled one wasted fit before the right one
  // (task 1350, as the Definition canvas does since task 1349). The pane WIDTH is
  // left out: "Path only" changes the shown steps, which refit on their own, and
  // the card's width only moves with the window, which changes its height too.
  const paneHeight = useStore((s) => s.height);

  // The `fitView` prop only fits on mount; refit when the shown steps or the measured pane height change.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      void fitView({ padding: PATH_FIT_PADDING, maxZoom: PATH_FIT_MAX_ZOOM, duration: 200 });
    });
    return () => cancelAnimationFrame(frame);
  }, [fitView, pathOnly, laidOut, paneHeight]);

  if (laidOut.nodes.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        The definition this run executed is not available, so the path cannot be drawn.
      </p>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 dark:text-gray-300">
          {(Object.keys(STEP_STATE_STYLES) as StepState[]).map((state) => (
            <span key={state} className="inline-flex items-center gap-1">
              <span className={`h-2 w-2 rounded-full ${STEP_STATE_STYLES[state].dot}`} aria-hidden="true" />
              {STEP_STATE_STYLES[state].label}
            </span>
          ))}
        </div>
        <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden text-xs" role="group" aria-label="Graph scope">
          <button
            type="button"
            aria-pressed={!pathOnly}
            onClick={() => setPathOnly(false)}
            className={`px-2.5 py-1 ${!pathOnly ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200'}`}
          >
            Full graph
          </button>
          <button
            type="button"
            aria-pressed={pathOnly}
            disabled={!pathOnlyAvailable}
            onClick={() => setPathOnly(true)}
            className={`px-2.5 py-1 ${
              !pathOnlyAvailable
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : pathOnly
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200'
            }`}
          >
            Path only
          </button>
        </div>
      </div>
      <div
        ref={wrapperRef}
        style={{ height }}
        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
        data-testid="run-path-canvas"
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          edgesReconnectable={false}
          elementsSelectable
          onNodeClick={(_, node) => onSelectStep(node.id)}
          fitView
          fitViewOptions={{ padding: PATH_FIT_PADDING, maxZoom: PATH_FIT_MAX_ZOOM }}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
    </div>
  );
}

/**
 * The run's path (task 1151, step 7): the definition the run executed, drawn
 * with the config editor's layout (`collaborationToReactFlow`, dagre when the
 * definition stores no positions), each step coloured by its state in the
 * selected turn. Read-only: no drag, no connect, no palette.
 */
export function RunPathCanvas(props: RunPathCanvasProps) {
  return (
    <ReactFlowProvider>
      <RunPathCanvasInner {...props} />
    </ReactFlowProvider>
  );
}
